// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import loading from '../../../share/components/vue/ProcessingIcon';
import { mapState, mapActions } from 'vuex';
import api from '../../../common/api';
import config from '../../../share/config';
import handler from '../../../share/util/apiResultValidator';

export default {
  data() {
    return {
      unitSettings: [],
      processing: false,
      params: null,
    };
  },

  components: {
    'processing-view': loading,
  },

  computed: {
    ...mapState({
      userProperty: state => state.userProperty,
      cachedPopUpMenuPrevious: state => state.cache.popUpMenuPreviousStores,
    }),
  },

  // 遷移してきた画面のパラメータ情報を保持
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // ポップアップメニュー内の画面以外の場合、 前画面情報をキャッシュに保持
      if (from.params && !config.POPUP_MENU_DISPLAY_NAME.includes(from.name)) {
        vm.cachePopUpMenuPreviousStores({ previousName: from.name, queryName: from.query, params: from.params });
      }
    });
  },

  methods: {
		...mapActions('auth', ['logout']),
    ...mapActions('cache', ['cachePopUpMenuPreviousStores']),

    /** 元の画面に戻る */
    backToPrevious: function () {
      // キャッシュから前画面情報取得
      this.params = this.cachedPopUpMenuPrevious.params;
      if (this.params) {
        this.params.previousDisplayName = 'changeUnit';
        this.$router.push({
          name: this.cachedPopUpMenuPrevious.previousName,
          params: this.params,
          query: this.cachedPopUpMenuPrevious.queryName
        });
      } else {
        this.$router.push({ name: this.cachedPopUpMenuPrevious.previousName, params: { previousDisplayName : 'changeUnit' } });
      }
    },

    /** 画面表示 */
    async setup() {
      // 単位設定情報の取得（バックエンドAPI_GET）
      this.processing = true;
      const response = await api.getCall(config.ADMIN, '/unitSettings');
      this.processing = false;

      // APIレスポンスの検証
      handler.validate(handler.validateTypes.all, response, this, null, () => {
        // 処理が成功したときのコールバック処理
        this.unitSettings = [{
          name: 'message.label_change_unit_distance',
          items: [
            { value: 'km', text: 'message.radio_change_unit_distance_km' },
            { value: 'mile', text: 'message.radio_change_unit_distance_mile' },
          ],
          selected: response.data.unit_settings.distance
        }, {
          name: 'message.label_change_unit_speed',
          items: [
            { value: 'km/h', text: 'message.radio_change_unit_speed_kmph' },
            { value: 'MPH', text: 'message.radio_change_unit_speed_mph' },
          ],
          selected: response.data.unit_settings.speed
        }, {
          name: 'message.label_change_unit_temperature',
          items: [
            { value: 'ﾟC', text: 'message.radio_change_unit_temperature_c' },
            { value: 'ﾟF', text: 'message.radio_change_unit_temperature_f' },
          ],
          selected: response.data.unit_settings.temperature
        }, {
          name: 'message.label_change_unit_pressure',
          items: [
            { value: 'kPa', text: 'message.radio_change_unit_pressure_kpa' },
            { value: 'mmHg', text: 'message.radio_change_unit_pressure_mmhg' },
            { value: 'inHg', text: 'message.radio_change_unit_pressure_inhg' },
            { value: 'psi', text: 'message.radio_change_unit_pressure_psi' },
            { value: 'inH2O', text: 'message.radio_change_unit_pressure_inh2o' },
          ],
          selected: response.data.unit_settings.pressure
        }, {
          name: 'message.radio_change_unit_flow_rate',
          items: [
            { value: 'g/s', text: 'message.radio_change_unit_flow_rate_gs' },
            { value: 'lb/min', text: 'message.radio_change_unit_flow_rate_lbmin' },
          ],
          selected: response.data.unit_settings.flow_rate
        }, {
          name: 'message.label_change_unit_weight',
          items: [
            { value: 'kg', text: 'message.radio_change_unit_weight_kg' },
            { value: 'lb', text: 'message.radio_change_unit_weight_lb' },
          ],
          selected: response.data.unit_settings.weight
        }, {
          name: 'message.label_change_unit_load',
          items: [
            { value: 'g/rev', text: 'message.radio_change_unit_load_grev' },
            { value: 'lb/rev', text: 'message.radio_change_unit_load_lbrev' },
          ],
          selected: response.data.unit_settings.load
        }];
      },
      (result) => {
        // 処理が失敗したときのコールバック処理
        if (result.behaviorType === handler.behaviorTypes.logout) {
          this.logout(this);
        }
      });
    },

    /** 更新ボタン押下 */
    async didTapChangeUnit() {
      if (this.processing) return;

      this.processing = true;
      const param = {
        unit_settings: {
          distance: this.unitSettings[this.unitSettings.findIndex(item => item.name === 'message.label_change_unit_distance')].selected,
          speed: this.unitSettings[this.unitSettings.findIndex(item => item.name === 'message.label_change_unit_speed')].selected,
          temperature: this.unitSettings[this.unitSettings.findIndex(item => item.name === 'message.label_change_unit_temperature')].selected,
          pressure: this.unitSettings[this.unitSettings.findIndex(item => item.name === 'message.label_change_unit_pressure')].selected,
          flow_rate: this.unitSettings[this.unitSettings.findIndex(item => item.name === 'message.radio_change_unit_flow_rate')].selected,
          weight: this.unitSettings[this.unitSettings.findIndex(item => item.name === 'message.label_change_unit_weight')].selected,
          load: this.unitSettings[this.unitSettings.findIndex(item => item.name === 'message.label_change_unit_load')].selected
        }
      };
      // 単位設定情報の更新（バックエンドAPI_POST）
      const response = await api.postCall(config.ADMIN, '/unitSettings', param);
      this.processing = false;

      // APIレスポンスの検証
      handler.validate(handler.validateTypes.all, response, this, null, () => {
        // 成功した場合
        this.backToPrevious();
      }, 
      (result) => {
        // 処理が失敗したときのコールバック処理
        if (result.behaviorType === handler.behaviorTypes.logout) {
          this.logout(this);
        }
      });
    },
  },

  async mounted() {
    // 画面表示
    await this.setup();
  }
};